import { useCallback, useState, useMemo } from "react"
import {
  Box,
  Stepper,
  Typography,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  useTheme,
  Backdrop,
  CircularProgress,
  StepConnector,
} from "@mui/material"
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "providers/i18n"
import _ from "lodash"
import { Trans } from "react-i18next"

import { useVenue } from "providers/venue"
import { useDirectionOverview } from "hooks/direction/useDirectionOverview"
import { useDirectionOverviewMap } from "hooks/direction/useDirectionOverviewMap"
import { SwipeableDrawer } from "components/SwipeableDrawer"
import { OrdinalSelector } from "components/OrdinalSelector"
import { OriginMarkerIcon } from "components/icons"
import {
  DirectionStepItem,
  DirectionCardActions,
} from "components/domains/direction"
import { DirectionSummary } from "extensions/iconsiam/components/domains/direction/DirectionSummary"
import { DestinationMarkerIcon } from "components/icons"
import { useAppUI } from "providers/ui"
import { getIcon } from "utils/icon"
import { DirectionSearchContainer } from "extensions/iconsiam/containers/DirectionSearchContainer"
export const DirectionOverviewPage = () => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { isMultiOrdinalVenue } = useVenue()

  const {
    isCalculatingRoute,
    overviewSteps,
    duration,
    distance,
    currentOrdinal,
    originData,
    originName,
    destinationData,
    destinationName,
    elevatorPreferredMode,
    relatedLevelWithSteps,
    scannedLocations,
    userLocation,
    route,
    changeOrdinal,
    toggleElevatorPreference,
    startNavigate,
  } = useDirectionOverview()

  useDirectionOverviewMap(route, currentOrdinal)

  const { t } = useTranslation()

  const { goBack } = useAppUI()

  const theme = useTheme()
  const { drawer: drawerProps } =
    theme.components[`AppDirectionOverview`]?.defaultProps || {}

  const { headerWrapper, paper, formControlLabel } =
    theme.components[`AppDirectionSearch`]?.styleOverrides || {}
  const {
    bleedWrapper,
    drawer,
    root,
    stepper,
    stepOverviewContainer,
    ordinalSelector,
    connector,
  } = theme.components[`AppDirectionOverview`]?.styleOverrides || {}

  const {
    originIcon,
    destinationIcon,
    highlightedText,
    directionStepIcon: directionStepIconStyle,
  } = theme.components[`DirectionStep`]?.styleOverrides || {}

  const destinationMarkerIcon = useMemo(
    () =>
      getIcon(theme?.assets?.directionStep?.destination, destinationIcon) || (
        <DestinationMarkerIcon
          color="error"
          fontSize="small"
          sx={destinationIcon}
        />
      ),
    [theme, destinationIcon]
  )

  const originMarkerIcon = useMemo(
    () =>
      getIcon(theme?.assets?.directionStep?.origin, originIcon) || (
        <OriginMarkerIcon sx={originIcon} />
      ),
    [theme, originIcon]
  )

  const directionStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.directionStep?.step, directionStepIconStyle) || (
        <DirectionsWalkIcon sx={directionStepIconStyle} />
      ),
    [theme, directionStepIconStyle]
  )

  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const handleOpenDrawer = useCallback(() => {
    setOpen(true)
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setOpen(false)
  }, [])

  const handleChange = useCallback(
    ({ origin, destination }) => {
      const originParam = origin?.id || "unknown"
      const destinationParam = destination?.id || "unknown"
      navigate(`/maps/dir/${originParam}/${destinationParam}`, {
        replace: true,
      })
    },
    [navigate]
  )

  const handleClickStep = useCallback(() => setOpen((prev) => !prev), [])
  const stepsAvailable = overviewSteps && overviewSteps.length > 0
  return (
    <>
      <Backdrop open={isCalculatingRoute}>
        <CircularProgress />
      </Backdrop>
      <Box sx={root}>
        <Box sx={headerWrapper}>
          <Stack spacing={2} alignItems="flex-start">
            <DirectionSearchContainer
              origin={originData}
              destination={destinationData}
              onChange={handleChange}
              onBack={handleClickBack}
              userLocation={userLocation}
              lastScannedLocation={_.last(scannedLocations)}
            />
            <Paper elevation={1} sx={paper}>
              <FormControlLabel
                control={
                  <Switch
                    checked={elevatorPreferredMode}
                    onChange={toggleElevatorPreference}
                  />
                }
                label={t("Avoid Steps")}
                componentsProps={{ typography: { variant: "body2" } }}
                sx={formControlLabel}
              />
            </Paper>
          </Stack>
        </Box>
        {/* //TODO:: find a way for adding active ordinal (selectedOrdinal) */}
        {isMultiOrdinalVenue && (
          <Box sx={ordinalSelector}>
            <OrdinalSelector
              label="Floor"
              levels={relatedLevelWithSteps}
              onClickOrdinal={changeOrdinal}
              selectedOrdinal={currentOrdinal}
            />
          </Box>
        )}
      </Box>

      {stepsAvailable && (
        <>
          {/* Direction Summary (mobile) */}
          <SwipeableDrawer
            {...drawerProps}
            open={open}
            onClose={handleCloseDrawer}
            onOpen={handleOpenDrawer}
            // contentProps={{ onTouchStart: () => {} }} This case will prevent the drawer from closing when dragging the drawer in the overview step to ensure the scrollbar in the DirectionStepItem section works properly
            sx={drawer}
          >
            <Box sx={bleedWrapper}>
              <DirectionSummary
                duration={duration}
                distance={distance}
                originName={originName}
                destinationName={destinationName}
              />
              <DirectionCardActions
                onStart={startNavigate}
                onStep={handleClickStep}
              />
            </Box>

            <Box sx={stepOverviewContainer}>
              <Stepper
                sx={stepper}
                orientation="vertical"
                connector={<StepConnector sx={connector} />}
              >
                {originData && (
                  <DirectionStepItem
                    primaryLabel={
                      <Trans
                        components={{
                          location: highlightedStepText,
                        }}
                      >
                        {isMultiOrdinalVenue
                          ? t("Location Floor", {
                              location: t(originName),
                              level: originData.level,
                            })
                          : t(originName)}
                      </Trans>
                    }
                    secondaryLabel={t("Your last scanned location")}
                    stepLabelProps={{
                      icon: originMarkerIcon,
                    }}
                  />
                )}
                <StepConnector sx={connector} />
                {overviewSteps.map((step, index) => {
                  const { description } = step
                  return (
                    <DirectionStepItem
                      index={index}
                      navigateTo={`navigate/steps/${
                        index + 1
                      }?avoidSteps=${elevatorPreferredMode}`}
                      primaryLabel={
                        <Trans
                          components={{
                            category: highlightedStepText,
                            destination: highlightedStepText,
                            landmark: highlightedStepText,
                            intermediary: highlightedStepText,
                            toward: highlightedStepText,
                            turn: highlightedStepText,
                          }}
                        >
                          {t(description.text, {
                            category: t(description.options?.category),
                            destination: t(description.options?.destination),
                            landmark: t(description.options?.landmark),
                            intermediary: t(description.options?.intermediary),
                            toward: t(description.options?.toward),
                            turn: t(description.options?.turn),
                          })}
                        </Trans>
                      }
                      key={`step-description-${index}`}
                      stepLabelProps={{
                        icon: directionStepIcon,
                      }}
                    />
                  )
                })}
                {destinationData && (
                  <DirectionStepItem
                    primaryLabel={
                      <Trans
                        components={{
                          location: highlightedStepText,
                        }}
                      >
                        {isMultiOrdinalVenue
                          ? t("Location Floor", {
                              location: t(destinationName),
                              level: destinationData.level,
                            })
                          : t(destinationName)}
                      </Trans>
                    }
                    secondaryLabel={t("Your Destination")}
                    stepLabelProps={{
                      icon: destinationMarkerIcon,
                    }}
                  />
                )}
              </Stepper>
            </Box>
          </SwipeableDrawer>
        </>
      )}
    </>
  )
}
